/**
 * "Where your key is used" — the site's origins, on the Sites & keys screen.
 *
 * Since migration 059 a site key works on ANY https website by default
 * (`origin_policy: 'any'`): pasting the snippet is the whole setup, so there
 * is no Domains step any more. The list fills itself — the server records
 * each website the first time the widget loads there — and it is where a
 * partner answers "someone copied my key": Block refuses one website under
 * either policy.
 *
 * "Only allow websites I list" (`origin_policy: 'listed'`) is optional. It
 * locks the LIVE key to the un-blocked rows the partner ADDED (or verified),
 * and reveals the add-a-website form and the "tried to load, refused" list
 * that only make sense once there is a lock. A row the widget was only SEEN
 * on does not count (migration 088): anyone can make the key "seen" on their
 * own site, so it gets an "Add to list" button instead. Turning the lock on
 * with no such website is refused by the server (ORIGIN_LIST_EMPTY) because
 * it would switch the widget off everywhere at once. A verified origin still
 * covers its subdomains under the lock, and the test key still works on
 * localhost.
 *
 * Below 720 px both lists are card rows (`usePhone`): as a table, Status and
 * the Block button — this panel's main action — sat off the right edge
 * behind a horizontal scroll at 390 px.
 */
import { useState, type FormEvent } from 'react';
import type { Origin, OriginPolicy, UnlistedAttempt } from '../api/types';
import { useSession } from '../auth/session';
import { useSites } from '../state/sites';
import { useLastSeen } from '../state/lastSeen';
import { Empty, ErrorNotice, Field, Loading, Switch, Time, useToast } from '../ui/kit';
import { useMutation, usePhone, useQuery } from '../ui/hooks';

function StatusTag({ origin, locked }: { origin: Origin; locked: boolean }) {
  if (origin.blocked_at) return <span className="tag bad">Blocked</span>;
  if (origin.verified_at) {
    return (
      <span
        className="tag ok"
        title={`Verified with the ownership file.${locked ? ' Verified websites also cover their subdomains.' : ''}`}
      >
        Verified with the file{locked ? ' · covers subdomains' : ''}
      </span>
    );
  }
  if (origin.listed_at) return <span className="tag ok">In your list</span>;
  // Only seen: allowed while the key works anywhere, refused once locked.
  return locked ? (
    <span className="tag bad" title="The widget was seen here, but you have not added this website to your list.">
      Not in your list
    </span>
  ) : (
    <span className="tag">Allowed</span>
  );
}

/** Seen by the widget, never added or verified, not blocked. */
function onlySeen(o: Origin): boolean {
  return !o.listed_at && !o.verified_at && !o.blocked_at;
}

export function KeyUsage({
  siteKey,
  testKey,
  policy,
}: {
  siteKey: string;
  testKey: string | null;
  policy: OriginPolicy;
}) {
  const { api } = useSession();
  const { reload: reloadSites } = useSites();
  const toast = useToast();
  const [value, setValue] = useState('');
  const locked = policy === 'listed';
  const origins = useQuery(() => api.listOrigins(siteKey), [siteKey], { pollMs: 60_000 });
  const attempts = useQuery(() => api.unlistedAttempts(siteKey), [siteKey], { enabled: locked, pollMs: 30_000 });
  const setPolicy = useMutation((next: OriginPolicy) => api.setOriginPolicy(siteKey, next));
  const setBlocked = useMutation((id: number, blocked: boolean) => api.setOriginBlocked(siteKey, id, blocked));
  const add = useMutation(async (origin: string) => {
    const row = await api.addOrigin(siteKey, origin);
    // Keep the test key in step so "test a lead" works on staging too.
    if (testKey) await api.addOrigin(testKey, origin).catch(() => undefined);
    return row;
  });
  const remove = useMutation((id: number) => api.removeOrigin(siteKey, id));
  const phone = usePhone();
  const lastSeen = useLastSeen();
  const seenAt = (o: Origin) => lastSeen.origin(siteKey, o.origin, o.last_seen_at);

  const submit = async (origin: string, e?: FormEvent) => {
    e?.preventDefault();
    let normalised = origin.trim();
    if (normalised && !/^[a-z]+:\/\//i.test(normalised)) normalised = `https://${normalised}`;
    const row = await add.run(normalised);
    if (row) {
      setValue('');
      toast(`${row.origin} can now use this key`);
      await Promise.all([origins.reload(), attempts.reload(), reloadSites()]);
    }
  };

  const toggleLock = async (next: boolean) => {
    const res = await setPolicy.run(next ? 'listed' : 'any');
    if (res) {
      toast(next ? 'Your key now only works on the websites you list' : 'Your key works on any website again');
      await reloadSites();
    }
  };

  const toggleBlock = async (o: Origin) => {
    const blocking = !o.blocked_at;
    if (blocking && !window.confirm(`Block ${o.origin}? Your widget stops loading there with this key. You can unblock it any time.`)) return;
    const res = await setBlocked.run(o.id, blocking);
    if (res) {
      toast(blocking ? `${o.origin} is blocked` : `${o.origin} is unblocked`);
      await Promise.all([origins.reload(), reloadSites()]);
    }
  };

  const originActions = (o: Origin) => (
    <>
      {onlySeen(o) ? (
        <button
          type="button"
          className="btn btn-sm"
          style={{ marginRight: 6 }}
          aria-label={`Add ${o.origin} to your list`}
          disabled={add.pending}
          onClick={() => void submit(o.origin)}
        >
          Add to list
        </button>
      ) : null}
      <button
        type="button"
        // Quiet until hovered: blocking is a per-row safety valve, not the
        // panel's call to action, and it asks before it acts.
        className={o.blocked_at ? 'btn btn-sm' : 'btn btn-sm btn-danger-subtle'}
        aria-label={`${o.blocked_at ? 'Unblock' : 'Block'} ${o.origin}`}
        disabled={setBlocked.pending}
        onClick={() => void toggleBlock(o)}
      >
        {o.blocked_at ? 'Unblock' : 'Block'}
      </button>
      {locked ? (
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          style={{ marginLeft: 6 }}
          aria-label={`Remove ${o.origin}`}
          disabled={remove.pending}
          onClick={async () => {
            if (!window.confirm(`Remove ${o.origin} from your list?`)) return;
            await remove.run(o.id);
            await Promise.all([origins.reload(), reloadSites()]);
          }}
        >
          Remove
        </button>
      ) : null}
    </>
  );

  const allowAction = (a: UnlistedAttempt) =>
    a.origin.startsWith('https://') ? (
      <button type="button" className="btn btn-sm" onClick={() => void submit(a.origin)}>
        Allow
      </button>
    ) : (
      <span className="small faint">http is never allowed</span>
    );

  const items = [...(origins.data?.items ?? [])].sort(
    (a, b) => (seenAt(b) ?? '').localeCompare(seenAt(a) ?? '') || a.id - b.id,
  );
  const listed = new Set(items.map((o) => o.origin));
  const unlisted = (attempts.data?.items ?? []).filter((a) => !listed.has(a.origin));

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-head">
          <h2>Where your key is used</h2>
          <span className="small muted">Changes reach live widgets within a minute.</span>
        </div>
        <div className="panel-body" style={{ paddingBottom: 0 }}>
          <p className="small muted" style={{ margin: 0 }}>
            {locked
              ? 'Your key only works on the websites below that are not blocked.'
              : 'Your key works on any website where the snippet is pasted. Each one shows up here the first time the widget loads on it.'}
          </p>
        </div>
        {origins.loading ? (
          <Loading />
        ) : origins.error ? (
          <div className="panel-body">
            <ErrorNotice error={origins.error} title="Could not load the websites" />
          </div>
        ) : items.length && phone ? (
          <div className="cards">
            {items.map((o) => (
              <div className="card" key={o.id}>
                <span className="mono wrap-anywhere" style={{ color: 'var(--ink)' }}>
                  {o.origin}
                </span>
                <span className="row" style={{ gap: 6 }}>
                  <StatusTag origin={o} locked={locked} />
                  <span className="small muted">
                    Last seen <Time iso={seenAt(o)} />
                  </span>
                </span>
                <span className="card-actions">{originActions(o)}</span>
              </div>
            ))}
          </div>
        ) : items.length ? (
          <div className="table-wrap">
            <table className="data">
              <caption className="visually-hidden">Websites using this key</caption>
              <thead>
                <tr>
                  <th scope="col">Website</th>
                  <th scope="col">Last seen</th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => (
                  <tr key={o.id}>
                    <td className="mono">{o.origin}</td>
                    <td>
                      <Time iso={seenAt(o)} />
                    </td>
                    <td>
                      <StatusTag origin={o} locked={locked} />
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{originActions(o)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty title="Not used anywhere yet">
            Paste the snippet on your website and open the page. It shows up here within a few minutes.
          </Empty>
        )}
        <ErrorNotice error={setBlocked.error ?? remove.error ?? (locked ? null : add.error)} />
      </section>

      <section className="panel panel-pad stack">
        <Switch
          label="Only allow websites I list"
          description={
            <>
              Optional. Anyone can read your key in your page source and paste it onto their own website. Turn this on
              and your live key only works on the websites you added to your list (or verified) that aren’t blocked —
              a website the widget was only seen on doesn’t count until you add it. Leave it off and it works
              anywhere you paste it; you can still block one website at a time. Your test key always works on
              localhost.
            </>
          }
          checked={locked}
          disabled={setPolicy.pending}
          onChange={(next) => void toggleLock(next)}
        />
        <ErrorNotice error={setPolicy.error} />
        {locked ? (
          <form className="row" style={{ alignItems: 'flex-end' }} onSubmit={(e) => void submit(value, e)}>
            <div className="grow">
              <Field label="Add a website" hint="Scheme and host only, e.g. https://www.example.com. https is required.">
                {(p) => (
                  <input
                    {...p}
                    className="input input-mono"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="https://www.example.com"
                  />
                )}
              </Field>
            </div>
            <button className="btn btn-primary" type="submit" disabled={add.pending || !value.trim()}>
              {add.pending ? 'Adding…' : 'Add website'}
            </button>
          </form>
        ) : null}
        {locked && add.error ? <ErrorNotice error={add.error} /> : null}
      </section>

      {locked && unlisted.length ? (
        <section className="panel" aria-labelledby="unlisted-h">
          <div className="panel-head">
            <h2 id="unlisted-h">Tried to use your key, and were refused</h2>
            <span className="small muted">Websites not on your list.</span>
          </div>
          {phone ? (
            <div className="cards">
              {unlisted.map((a) => (
                <div className="card" key={a.origin}>
                  <span className="mono wrap-anywhere" style={{ color: 'var(--ink)' }}>
                    {a.origin}
                  </span>
                  <span className="small muted">
                    {a.attempt_count} {a.attempt_count === 1 ? 'attempt' : 'attempts'} · last <Time iso={a.last_seen_at} />
                  </span>
                  <span className="card-actions">{allowAction(a)}</span>
                </div>
              ))}
            </div>
          ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Website</th>
                  <th scope="col" className="num">
                    Attempts
                  </th>
                  <th scope="col">Last attempt</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {unlisted.map((a) => (
                  <tr key={a.origin}>
                    <td className="mono">{a.origin}</td>
                    <td className="num">{a.attempt_count}</td>
                    <td>
                      <Time iso={a.last_seen_at} />
                    </td>
                    <td style={{ textAlign: 'right' }}>{allowAction(a)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
